import type { CardItem, PalworldItem } from "@/interfaces/CardItem";
import type { DeckBuilderFilterSpec } from "@/components/DeckBuilder/filters/spec";

/**
 * Palworld's deck builder filters.
 *
 * Everything the builder offers for Palworld is declared here -- the bar, the
 * default state and the filtering all come off this one list. See
 * components/DeckBuilder/filters/spec.ts for what a field may declare.
 *
 * Fields whose values are open-ended (cost, power, attack, rarity, set) declare
 * no options, so they are derived from the card pool: a new set or a new cost
 * appears in its filter as soon as the scrape has a card with it.
 */

const pw = (card: CardItem) => card as PalworldItem;

/** The five colours in print. Fixed, so they stay in this order in the menu. */
const COLOR_OPTIONS = [
  { key: "Red", label: "Red", swatch: "bg-red-500" },
  { key: "Blue", label: "Blue", swatch: "bg-blue-500" },
  { key: "Green", label: "Green", swatch: "bg-green-500" },
  { key: "Purple", label: "Purple", swatch: "bg-purple-500" },
  { key: "Colorless", label: "Colorless", swatch: "bg-slate-400" },
];

/** Card kinds, in the order the rulebook introduces them. */
const CARD_KIND_OPTIONS = [
  { key: "Pal", label: "Pal" },
  { key: "Structure", label: "Structure" },
  { key: "Event", label: "Event" },
  { key: "Gear", label: "Gear" },
  { key: "Soul", label: "Soul" },
];

/** Rarity runs from common to the one-of SSS, not alphabetically. */
const RARITY_ORDER = [
  "C",
  "U",
  "R",
  "RR",
  "SR",
  "SP",
  "SSP",
  "SSS",
  "OSR",
  "TD",
  "TSR",
  "TSP",
  "PR",
];

export const palworldFilterSpec: DeckBuilderFilterSpec = [
  {
    kind: "search",
    fields: [
      { key: "name", label: "Name", textOf: (card) => pw(card).name ?? "" },
      { key: "effect", label: "Effect", textOf: (card) => pw(card).text ?? "" },
      {
        key: "type",
        label: "Pal Type",
        textOf: (card) => (pw(card).type ?? []).join(" "),
      },
      {
        key: "aptitude",
        label: "Aptitude",
        textOf: (card) => (pw(card).aptitude ?? []).join(" "),
      },
      {
        key: "number",
        label: "Card No.",
        textOf: (card) => pw(card).card_number ?? "",
      },
    ],
  },
  {
    kind: "select",
    key: "color",
    label: "Color",
    options: COLOR_OPTIONS,
    width: "w-[125px]",
    valuesOf: (card) => [pw(card).color],
  },
  {
    kind: "select",
    key: "cardKind",
    label: "Card Kind",
    options: CARD_KIND_OPTIONS,
    width: "w-[135px]",
    valuesOf: (card) => [pw(card).card_kind],
  },
  {
    // Normal Pal vs Lucky Pal. Only Pals have one, so this narrows within them.
    kind: "select",
    key: "cardKindSub",
    label: "Pal Kind",
    width: "w-[125px]",
    valuesOf: (card) => [pw(card).card_kind_sub],
  },
  {
    // Elements: Fire, Water, Dark and so on. A Pal can have more than one.
    kind: "select",
    key: "type",
    label: "Pal Type",
    width: "w-[125px]",
    valuesOf: (card) => pw(card).type ?? [],
  },
  {
    // Work aptitudes: Kindling, Watering, Handiwork. Also multi-valued.
    kind: "select",
    key: "aptitude",
    label: "Aptitude",
    width: "w-[130px]",
    valuesOf: (card) => pw(card).aptitude ?? [],
  },
  {
    kind: "select",
    key: "cost",
    label: "Cost",
    width: "w-[95px]",
    valuesOf: (card) => [pw(card).cost],
  },
  {
    kind: "select",
    key: "power",
    label: "Power",
    width: "w-[105px]",
    valuesOf: (card) => [pw(card).power],
  },
  {
    kind: "select",
    key: "attack",
    label: "Attack",
    width: "w-[105px]",
    valuesOf: (card) => [pw(card).attack],
  },
  {
    kind: "select",
    key: "rarity",
    label: "Rarity",
    width: "w-[105px]",
    // Derived from the pool so a new rarity still appears, but ordered by the
    // known progression; anything unrecognised sorts to the end.
    compareOptions: (a, b) => {
      const rank = (value: string) => {
        const index = RARITY_ORDER.indexOf(value);
        return index === -1 ? RARITY_ORDER.length : index;
      };
      return rank(a) - rank(b) || a.localeCompare(b);
    },
    valuesOf: (card) => [pw(card).rarity],
  },
  {
    kind: "select",
    key: "set",
    label: "Set",
    width: "w-[115px]",
    // A reprint belongs to every set it was printed in, so it shows under each.
    valuesOf: (card) => {
      const item = pw(card);
      return item.sets?.length ? item.sets : [item.set_id];
    },
  },
  {
    kind: "toggle",
    key: "showAAs",
    label: "Show AAs",
    defaultValue: true,
  },
];
